/**
 * List filters bound to URL search params.
 */
import type { Option } from "@shared/constants.ts";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useUrlState } from "../../lib/url-state.ts";
import { Icon } from "./Icon.tsx";

export function SearchInput({
  param = "q",
  placeholder = "Search…",
}: {
  param?: string;
  placeholder?: string;
}) {
  const { get, set } = useUrlState();
  const urlValue = get(param);
  const [value, setValue] = useState(urlValue);
  // The last value both sides agree on. A URL change we didn't make (navigation) resets the
  // box; typing is written to the URL after a debounce.
  const synced = useRef(urlValue);
  useEffect(() => {
    if (urlValue === synced.current) return;
    synced.current = urlValue;
    setValue(urlValue);
  }, [urlValue]);
  useEffect(() => {
    if (value === synced.current) return;
    const t = setTimeout(() => {
      synced.current = value;
      set({ [param]: value });
    }, 300);
    return () => clearTimeout(t);
  }, [value, param, set]);
  return (
    <div className="position-relative" style={{ minWidth: 200, flex: "1 1 220px", maxWidth: 360 }}>
      <span className="position-absolute top-50 translate-middle-y ms-2 text-muted">
        <Icon name="search" size={14} />
      </span>
      <Form.Control
        size="sm"
        type="search"
        className="ps-4"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  );
}

export function FilterSelect({
  param,
  label,
  options,
  allLabel = "All",
}: {
  param: string;
  label: string;
  options: ReadonlyArray<Option>;
  allLabel?: string;
}) {
  const { get, set } = useUrlState();
  return (
    <Form.Select
      size="sm"
      aria-label={label}
      value={get(param)}
      onChange={(e) => set({ [param]: e.target.value })}
      style={{ width: "auto", maxWidth: 240 }}
    >
      <option value="">
        {label}: {allLabel}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Form.Select>
  );
}

export function FilterBar({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="d-flex flex-wrap gap-2 align-items-center p-3 border-bottom bg-light-subtle">
      {children}
      {actions && <div className="ms-auto d-flex gap-2">{actions}</div>}
    </div>
  );
}

/** Segmented tabs bound to a URL param (e.g. task views). */
export function ViewTabs({
  param,
  options,
  defaultValue,
}: {
  param: string;
  options: ReadonlyArray<Option>;
  defaultValue: string;
}) {
  const { get, set } = useUrlState();
  const current = get(param) || defaultValue;
  return (
    <fieldset className="btn-group btn-group-sm flex-wrap border-0 p-0 m-0">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`btn ${current === o.value ? "btn-primary" : "btn-outline-secondary"}`}
          aria-pressed={current === o.value}
          onClick={() => set({ [param]: o.value === defaultValue ? null : o.value })}
        >
          {o.label}
        </button>
      ))}
    </fieldset>
  );
}
